import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { AuthService } from '../auth/auth.service';
import { routes } from '../../../consts';
import { UserStateService } from 'src/app/services/common/user/user-state.service';
import { TokenStorageService } from '../auth/token-storage.service';

@Injectable({
  providedIn: 'root',
})
export class AuthGuardService {
  public routers: typeof routes = routes;

  constructor(
    private router: Router,
    private authService: AuthService,
    private userStateService: UserStateService,
    private tokenStorage: TokenStorageService,
  ) {}

  async canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Promise<boolean> {
    const accessToken = this.tokenStorage.getActiveToken();
    const userId = this.tokenStorage.getUserId();

    try {
      if (!accessToken || !userId) {
        this.router.navigate(['/auth/login']);
        return false;
      }
      const user = await this.userStateService.loadUser(userId);
      const userEmail = user?.user?.email;
      if (!userEmail) {
        return true;
      }
      const isSharedWithUser = await this.authService.checkSharedWithUser(
        userEmail,
      );

      if (user.user.subStatus === 'active' || isSharedWithUser) {
        return true;
      } else {
        this.router.navigate(['/buyPlan']);
        return false;
      }
    } catch (error: any) {
      console.error('Auth Guard Error:', error);
      if (error.status === 401 || error?.error?.message?.includes('JWT')) {
        this.tokenStorage.clearAuth();
        sessionStorage.clear();
        this.userStateService.clearUser();
        this.router.navigate(['/auth/login']);
      } else {
        this.router.navigate(['/buyPlan']);
      }
      return false;
    }
  }
}

@Injectable({
  providedIn: 'root',
})
export class AuthGuardBuyPlanService {
  constructor(
    private router: Router,
    private tokenStorage: TokenStorageService,
  ) {}

  async canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Promise<boolean> {
    const accessToken = this.tokenStorage.getAccessToken();
    const userId = this.tokenStorage.getUserId();

    if (!accessToken || !userId) {
      this.router.navigate(['/auth/login']);
      return false;
    }

    return true;
  }
}

@Injectable({
  providedIn: 'root',
})
export class AdminGuard {
  constructor(
    private router: Router,
    private tokenStorage: TokenStorageService,
    private userStateService: UserStateService,
    private authService: AuthService,
  ) {}

  async canActivate(): Promise<boolean> {
    const accessToken = this.tokenStorage.getAccessToken();
    const userId = this.tokenStorage.getUserId();

    if (!accessToken || !userId) {
      this.router.navigate(['/auth/login']);
      return false;
    }

    try {
      const userState = await this.userStateService.loadUser(userId);
      const roles = userState?.user?.roles ?? [];
      if (roles.includes('admin')) {
        return true;
      }

      const response = await this.authService
        .getCurrentUser(userId)
        .toPromise();
      const apiRoles = response?.user?.roles ?? [];
      if (apiRoles.includes('admin')) {
        return true;
      }

      this.router.navigate(['/home']);
      return false;
    } catch {
      this.router.navigate(['/auth/login']);
      return false;
    }
  }
}

@Injectable({
  providedIn: 'root',
})
export class NonAuthGuardService {
  constructor(
    private router: Router,
    private tokenStorage: TokenStorageService,
  ) {}

  canActivate() {
    const accessToken = this.tokenStorage.getAccessToken();
    const userId = this.tokenStorage.getUserId();

    if (accessToken && userId) {
      this.router.navigate(['/home']);
      return false;
    }
    return true;
  }
}
